// TIP-609 — Counterparty Briefs: ALL static data for the page.
//
// This is the only file that knows there is no backend yet. Everything else
// consumes the shape below, which mirrors the agreed TIP-607 allowance
// response. When the endpoint lands, delete this file and have
// useCounterpartyBriefs.js call the API — no component changes.
//
// Every number and string here comes from the flow guideline dated 24 Aug 2026
// ("The rules box", page 3, and the copy appendix, pages 43 to 56). Nothing is
// invented. Where this file deliberately departs from the design, the ticket
// says so and the departure is commented.

// The rules box, exactly. These are config, never literals in a component —
// the claim gate's locked strip reads them too, so the panel cannot go stale
// the day a price changes.
export const ENTITLEMENTS = {
  claimed: { monthlyAllowance: 20, weeklyCap: 5, unit: "AED 149", unitLabel: "AED 149 per brief" },
  member: { monthlyAllowance: 30, weeklyCap: null, unit: "AED 134.10", unitLabel: "AED 134.10 per brief" },
  membership: { annual: "AED 999", monthly: "AED 99" },
};

// Both reset dates come from ONE backend value each and appear in the card, the
// banners, the confirmation and the buy modal. Never computed in the frontend —
// open questions 7 and 8 are still unanswered, so a second calculation here
// would disagree with the backend on screen.
export const RESET_DATES = {
  resetDate: "1 September",
  weekResetDate: "Monday 24 August",
};

// The claimed business whose allowance this page shows.
export const BUSINESS = { name: "Al Futtaim Logistics LLC" };

// The claim gate's CTAs are href="#" stubs in the design, with no destinations
// given. They are filled in here as the claim-flow entry points get confirmed.
//
// `Find your business` goes to the directory search: claiming starts by
// finding the listing, which is what step 1 of the gate tells the customer to
// do, and /search is where the rest of the dashboard sends them for the same
// thing (CompaniesYouFollow, PurchasedCompany and FollowNewCompanies all do).
//
// The other three are still unconfirmed. `go()` in the page ignores "#", so an
// unfilled one is an inert button rather than a navigation to nowhere.
export const GATE_ROUTES = {
  findBusiness: "/search",
  whatIsABrief: "#",
  claimStatus: "#",
  sendDocument: "#",
};

// The claim gate, verbatim. Both variants share one layout and one DOM; the
// accent colour and every string swap.
export const GATE_COPY = {
  unclaimed: {
    pillLabel: "Not claimed",
    headCompany: "No business claimed yet",
    eyebrow: "Claim your business",
    title: "Claim your business to order briefs",
    body:
      "Counterparty Briefs sit on top of a claimed business profile. Claiming tells us who you are and who you trade as, so a brief can be issued in your business name. It is free and it takes a few minutes.",
    lockNote: "Your allowance opens the moment your claim is approved",
    cta: "Find your business",
    ctaTwo: "What is a Counterparty Brief?",
    tableNote: "Briefs you order will appear here, with the PDF ready to download.",
    steps: [
      { n: "1", state: "todo", title: "Find your listing", body: "Search the directory. Most UAE businesses are already listed." },
      { n: "2", state: "todo", title: "Prove you work there", body: "A business email on the company domain, plus the trade licence number." },
      { n: "3", state: "todo", title: "We approve it", body: "Usually within one working day. Then 20 briefs a month are yours." },
    ],
  },
  pending: {
    pillLabel: "Claim in review",
    headCompany: "Al Futtaim Logistics LLC",
    eyebrow: "Claim in review",
    title: "We are checking your claim",
    body:
      "Your claim on Al Futtaim Logistics LLC is with our team. We check the trade licence and the email domain, usually within one working day. You will get an email the moment it is approved, and your allowance opens straight away.",
    lockNote: "Locked until your claim is approved",
    cta: "View claim status",
    ctaTwo: "Send another document",
    tableNote: "Order your first brief once your claim is approved.",
    steps: [
      { n: "\u2713", state: "done", title: "Listing found", body: "Al Futtaim Logistics LLC, Dubai mainland." },
      { n: "\u2713", state: "done", title: "Documents received", body: "Trade licence and faraz@trevex.io, submitted 17 Aug 2026." },
      { n: "3", state: "now", title: "Our check", body: "In progress. We will email you when it is done." },
    ],
  },
  // The locked strip. Numbers read from ENTITLEMENTS; only the trailing words
  // are copy. Built as drawn these were literals, which would make the panel
  // lie to every prospective customer the day a price changes.
  lockedStrip: [
    { eyebrow: "This week", key: "weeklyCap", note: "briefs a week" },
    { eyebrow: "This month", key: "monthlyAllowance", note: "briefs a month, free" },
    { eyebrow: "Extra briefs", key: "unit", note: "each, no expiry" },
  ],
  tableHeading: "Your briefs",
  // The gate's shell uses different column headings from the real table
  // (Company / Reference vs Target company / Country). Reproduced as drawn and
  // flagged: the ticket lists it as a decision, not a fix.
  tableColumns: ["Company", "Reference", "Ordered", "Completed", "Status"],
  tableEmptyHeading: "Nothing here yet.",
};

// Customer-facing chips. The filter uses shorter labels for the same four
// states — inconsistency 13, reproduced as designed rather than harmonised.
export const BRIEF_STATUSES = {
  RECEIVED: "Request received",
  BUILDING: "Building your brief",
  READY: "Ready",
  SNAG: "Snag. Credit returned",
};

export const FILTER_LABELS = {
  [BRIEF_STATUSES.RECEIVED]: "Received",
  [BRIEF_STATUSES.BUILDING]: "Building",
  [BRIEF_STATUSES.READY]: "Ready",
  [BRIEF_STATUSES.SNAG]: "Snag",
};

export const ALL_STATUSES = "All statuses";

export const PAGE_COPY = {
  title: "Counterparty Briefs",
  standfirst:
    "A plain-language read on a company you are about to trade with. Order one, TREVEX AI builds it, you download the PDF.",
  sharingNote: "Allowance is shared across everyone at this business",
  footer:
    "Briefs state facts, not credit advice. They are for your own trading decisions: no resale, no bulk harvesting.",
  tierPill: { claimed: "Claimed", member: "Member" },
  // TIP-609 — the two states the design has no screen for, because it was
  // drawn against fixtures. Neither invents a number: a failed allowance read
  // must never be shown as an allowance of zero.
  loading: "Loading your allowance\u2026",
  errorHeading: "We could not load your allowance",
  errorBody:
    "Something went wrong on our side, so we are not showing you a figure we cannot stand behind. Try again in a moment.",
  errorRetry: "Try again",
};

export const CARD_COPY = {
  weekEyebrow: "This week",
  monthEyebrow: "This month",
  boughtEyebrow: "Bought",
  // The number and its tail are separate spans on one baseline: 28px for the
  // figure, 14px for "of 20 left".
  weekTail: (cap) => `of ${cap} left`,
  monthTail: (allowance) => `of ${allowance} left`,
  boughtTail: "available",
  weekNote: (weekResetDate) => `Resets ${weekResetDate}`,
  weekNoteCapped: (weekResetDate) => `Opens again ${weekResetDate}`,
  monthNote: (resetDate) => `Resets ${resetDate}`,
  monthNoteWeekCapped: "Held back by the weekly limit",
  // TIP-609 fix: the design drops "any day" at one brief, exactly when the
  // promise matters most (inconsistency 17). One string at every count.
  boughtNote: "Ready to use, any day",
  order: "Order a brief",
  buyOne: "Buy a brief",
  becomeMember: "Become a Member",
};

export const BANNER_COPY = {
  amber: {
    heading: (weeklyCap) => `You have used your ${weeklyCap} briefs for this week`,
    body: ({ remaining, weekResetDate, unitLabel }) =>
      `You still have ${remaining} briefs in this month's allowance. They open up again ${weekResetDate}. To order one today, buy a brief at ${unitLabel}. Members have no weekly limit, so all 30 briefs are available any day.`,
    primary: "Buy a brief",
  },
  coral: {
    heading: "You are out of briefs this month",
    // The membership half of this sentence is suppressed for a Member — the
    // design sells them all three benefits they already hold (inconsistency 1).
    body: ({ resetDate, unitLabel, memberUnit, showMembershipPitch }) =>
      showMembershipPitch
        ? `Your allowance resets on ${resetDate}. Buy extra briefs at ${unitLabel}, or become a Member for 30 briefs a month, no weekly limit, and ${memberUnit} on extras.`
        : `Your allowance resets on ${resetDate}. Buy extra briefs at ${unitLabel}.`,
    primary: "Buy briefs",
  },
  secondary: "Become a Member",
};

// TIP-613 — the brief-ready email's deep link, when it cannot be followed.
//
// ONE message for both "no such reference" and "not your company". Telling
// them apart would let a stranger with a guessed reference learn which ones
// are real.
export const DEEP_LINK_COPY = {
  unavailable: "We could not open that brief. It may have been ordered by another business.",
  failed: "We could not open that brief just now. Your briefs are listed below.",
  dismiss: "Dismiss",
  /**
   * A DELIVERED brief arrived at from the email.
   *
   * The status modal is not the target for this one: STATUS_MODAL_COPY has
   * entries for Received, Building and Snag but none for Ready, so the modal
   * renders nothing for a finished brief. Which is reasonable — there is
   * nothing to explain, only a file to fetch — and it is exactly what the
   * email promised ("View and download your brief").
   *
   * Not a bug, and not a dead click either: StatusChip's own CLICKABLE list
   * excludes Ready, so that chip renders as a span rather than a button. The
   * table is already consistent with this; only the deep link needed a
   * destination of its own.
   *
   * A panel rather than a highlighted row, because the list pages on the
   * server and the brief may be on page four.
   */
  readyHeading: (reference) => `${reference} is ready`,
  readyBody: (company) => `Your brief on ${company} is ready to download.`,
  download: "Download the brief",
};

export const TABLE_COPY = {
  heading: "Your briefs",
  searchPlaceholder: "Search company or country",
  columns: ["Target company", "Country", "Ordered", "Completed", "Status"],
  download: "Download",
  // While the request is out. The button holds its place rather than being
  // replaced, so the row does not reflow mid-click.
  downloading: "Downloading\u2026",
  // TIP-610 — why a download did not happen. Keyed by case in
  // describeDownloadError; every one says whether trying again would help.
  downloadErrorDismiss: "Dismiss",
  downloadError: {
    offline: "The download did not start. Check your connection and try again.",
    signedOut: "Your session has expired. Sign in again to download this brief.",
    notReady:
      "This brief is not ready to download yet. We have refreshed the list \u2014 the button will be back when the PDF is in.",
    forbidden: "You do not have permission to download this brief.",
    missing: "We could not find the PDF for this brief. Contact us and we will sort it out.",
    failed: "The download failed. Try again, and tell us if it keeps happening.",
  },
  chipTooltip: "What does this mean?",
  filterAll: "All statuses",
  noMatchHeading: "No briefs match this view.",
  noMatchBody: "Try another status, or clear the search.",
  emptyBody:
    "Order a brief on a company you are about to trade with. Every brief you order appears here.",
  emptyAction: "Order your first brief",
  // TIP-609 fix: "to", not an en dash (inconsistency 22).
  paging: (from, to, total) => `Showing ${from} to ${to} of ${total}`,
  previous: "Previous",
  next: "Next",
  // TIP-610 — the table's own two states. It is a separate read from the
  // allowance card, so it can be loading or broken while the counters are fine.
  loading: "Loading your briefs\u2026",
  errorHeading: "We could not load your briefs",
  errorBody:
    "Your allowance above is correct. The list of briefs did not load, so nothing is shown rather than a partial list.",
  errorRetry: "Try again",
};

export const STATUS_MODAL_COPY = {
  eyebrow: "Status",
  [BRIEF_STATUSES.RECEIVED]: {
    heading: "TREVEX has received your request",
    body: (company) =>
      `Your order for ${company} is accepted and in the queue. Nothing is needed from you. One brief has come off this month's allowance, and it goes back if we cannot produce it.`,
    step2Meta: "Next",
  },
  [BRIEF_STATUSES.BUILDING]: {
    heading: "TREVEX AI is building your brief",
    body: (company) =>
      `Work on ${company} is underway. You do not need to stay on this page. This row updates on its own and the download button appears when the PDF is ready.`,
    step2Meta: "Now",
  },
  // TIP-609 fix: this chip IS clickable and carries the analyst's reason. The
  // design leaves it inert with no explanation anywhere (inconsistency 7), so
  // the row silently loses a brief with no account of why.
  [BRIEF_STATUSES.SNAG]: {
    heading: "We could not produce this brief",
    body: (company) =>
      `TREVEX could not complete the brief on ${company}. One brief has gone back to your allowance, and you can order again whenever you like.`,
    step2Meta: null,
  },
  steps: {
    one: { label: "Order accepted.", detail: "One brief used from this month's allowance." },
    two: {
      label: "Brief being built.",
      detail:
        "Licence records, filings, web presence and payment signals are read and written into the brief.",
    },
    three: {
      label: "Ready to download.",
      detail:
        "The PDF appears on this row. If the company cannot be identified, we say so and return the brief to your allowance.",
      meta: "Within 1 business day",
    },
  },
  footnote: "Every brief is fulfilled within one business day of the order, Dubai time.",
  // TIP-610 — the first tracker line renders from the source bucket. The
  // design says "Paid and queued." on every order, but most briefs come out of
  // the monthly allowance and involve no payment step at all.
  source: {
    plan: "Brief drawn from monthly allowance. Queued.",
    bought: "Bought brief used. Queued.",
  },
  // TIP-610 — what the detail endpoint adds, once it lands. Labels only; every
  // value is the customer's own from the order form, echoed back.
  detail: {
    heading: "What you asked for",
    company: "Company",
    purpose: "Purpose",
    requester: "Requested by",
    notes: "Your note to the analyst",
    loading: "Loading the rest of this order\u2026",
  },
  // The analyst's own words. Falls back to the design's default return reason.
  defaultSnagReason: "The business could not be identified with confidence.",
};

// Fourteen rows, so the paging label reads "Showing 1 to 10 of 14" as designed.
// The Completed cell is null where the design puts a literal em dash in the
// data (inconsistency 21) — see BriefsTable for how that renders.
export const BRIEFS = [
  { id: "TRX-CB-11827", company: "Gulf Steel Trading FZE", country: "United Arab Emirates", status: BRIEF_STATUSES.RECEIVED, ordered: "12 Aug 2026", completed: null },
  { id: "TRX-CB-11826", company: "Meridian Freight Services LLC", country: "United Arab Emirates", status: BRIEF_STATUSES.BUILDING, ordered: "11 Aug 2026", completed: null },
  { id: "TRX-CB-11824", company: "Nakheel Medical Supplies", country: "United Arab Emirates", status: BRIEF_STATUSES.READY, ordered: "9 Aug 2026", completed: "9 Aug 2026, 04:12 PM" },
  { id: "TRX-CB-11821", company: "Orient Polymers Pvt Ltd", country: "India", status: BRIEF_STATUSES.READY, ordered: "8 Aug 2026", completed: "7 Aug 2026, 09:35 AM" },
  { id: "TRX-CB-11818", company: "Unnamed Trading Co", country: "Oman", status: BRIEF_STATUSES.SNAG, ordered: "4 Aug 2026", completed: null, snagReason: "No trade licence found matching this name and number in Oman." },
  { id: "TRX-CB-11815", company: "Sharjah Cables Industries", country: "United Arab Emirates", status: BRIEF_STATUSES.READY, ordered: "3 Aug 2026", completed: "4 Aug 2026, 10:20 AM" },
  { id: "TRX-CB-11812", company: "Karachi Marine Logistics", country: "Pakistan", status: BRIEF_STATUSES.READY, ordered: "2 Aug 2026", completed: "3 Aug 2026, 02:45 PM" },
  { id: "TRX-CB-11809", company: "Levant Textiles SAL", country: "Lebanon", status: BRIEF_STATUSES.READY, ordered: "1 Aug 2026", completed: "2 Aug 2026, 11:05 AM" },
  { id: "TRX-CB-11806", company: "Anka Makina Ltd", country: "Turkiye", status: BRIEF_STATUSES.READY, ordered: "31 Jul 2026", completed: "1 Aug 2026, 09:15 AM" },
  { id: "TRX-CB-11803", company: "Deira Auto Spare Parts", country: "United Arab Emirates", status: BRIEF_STATUSES.SNAG, ordered: "30 Jul 2026", completed: null, snagReason: "The registered legal name did not match any licence on record." },
  { id: "TRX-CB-11800", company: "Emirates Modular Interiors", country: "United Arab Emirates", status: BRIEF_STATUSES.READY, ordered: "29 Jul 2026", completed: "30 Jul 2026, 04:50 PM" },
  { id: "TRX-CB-11797", company: "Nile Delta Agri Exports", country: "Egypt", status: BRIEF_STATUSES.READY, ordered: "28 Jul 2026", completed: "29 Jul 2026, 01:30 PM" },
  { id: "TRX-CB-11794", company: "Colombo Tea Traders", country: "Sri Lanka", status: BRIEF_STATUSES.READY, ordered: "27 Jul 2026", completed: "28 Jul 2026, 10:40 AM" },
  { id: "TRX-CB-11791", company: "Jebel Ali Cold Chain LLC", country: "United Arab Emirates", status: BRIEF_STATUSES.READY, ordered: "26 Jul 2026", completed: "27 Jul 2026, 03:20 PM" },
];
